/**
 * Supreme Concrete — location state.
 * Single source of truth for per-location contact data + cookie helpers.
 * Loaded globally from layout/theme.liquid.
 */
(function () {
  'use strict';

  // ─────────────────────────────────────────────────────────────────────────
  //  PRICING CONFIG — single source of truth for all calculators.
  //  Calculators (main-custom.js home calc, sections/price-list.liquid) MUST
  //  read these via SCLocation.pricing(). Do NOT inline pricing constants.
  //
  //  When the NY ops team finalises the formula, edit DATA.ny.pricing here —
  //  no calculator JS changes needed.
  // ─────────────────────────────────────────────────────────────────────────
  var DATA = {
    ny: {
      key: 'ny',
      label: 'New York',
      // Phone lives in snippets/sc-phone-links.liquid — see hydrate().
      address: '235 W 109th St Apt 6C, New York, NY 10025',
      pageUrl: '/pages/locations-new-york',
      pricing: {
        // ⚠️ NY pricing — PLACEHOLDER. Mirrors LA until the NY ops formula is finalised.
        // Anything off-spec for NY → adjust below; calculators auto-pick it up.
        taxPercent: 0.095,            // TODO: set NY combined sales tax (likely 0.08875 NYC)
        envFeePerTrip: 55,            // TODO: confirm NY environmental fee
        pumpFlat: 300,                // TODO: confirm NY pump delivery & setup
        pumpPerYard: 13,              // TODO: confirm NY pumping per yard
        // unitPriceMultiplier scales the per-yard concrete price BEFORE tax & volume.
        // 1.0 = no change. Use this for the NY market coefficient when ops gives it.
        unitPriceMultiplier: 1.0,     // TODO: set NY price coefficient
        // deliveryMultiplier scales the per-trip delivery rate. 1.0 = no change.
        deliveryMultiplier: 1.0,      // TODO: set NY delivery scale if different
        defaultDeliveryPrice: 300,    // fallback when distance can't be resolved
        // Google Maps DistanceMatrix origin for this plant. The destination is built
        // from the ZIP alone — see distanceMiles(). Never prepend a city here.
        mapsOrigin: 'New York, NY 10025, USA'      // TODO: real NY plant address
      }
    },
    la: {
      key: 'la',
      label: 'Los Angeles',
      // Phone lives in snippets/sc-phone-links.liquid — see hydrate().
      address: '505 N Figueroa, Los Angeles, California, 90012',
      pageUrl: '/pages/locations-los-angeles',
      pricing: {
        taxPercent: 0.095,
        envFeePerTrip: 55,
        pumpFlat: 300,
        pumpPerYard: 13,
        unitPriceMultiplier: 1.0,
        deliveryMultiplier: 1.0,
        defaultDeliveryPrice: 300,
        mapsOrigin: 'Los Angeles, CA 91352, USA'
      }
    }
  };

  var COOKIE = 'sc_location';
  var DEFAULT = 'la';
  var ONE_YEAR = 60 * 60 * 24 * 365;

  function readCookie() {
    var match = document.cookie.match(/(?:^|;\s*)sc_location=(ny|la)(?:;|$)/);
    return match ? match[1] : null;
  }

  function writeCookie(value) {
    if (value !== 'ny' && value !== 'la') return;
    var secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      COOKIE + '=' + value +
      '; path=/; max-age=' + ONE_YEAR +
      '; SameSite=Lax' + secure;
  }

  function current() {
    return readCookie() || DEFAULT;
  }

  function get(key) {
    return DATA[key] || DATA[DEFAULT];
  }

  function hydrate(root) {
    root = root || document;
    var key = current();
    var loc = get(key);

    // ⚠️ Phone numbers are deliberately ABSENT from this function.
    //
    // They are rendered server-side by snippets/sc-phone-links.liquid (both
    // variants, CSS picks one) precisely so that NOTHING here writes them.
    // Google Ads call tracking (AW-746720555) rewrites tel: hrefs at runtime to
    // a forwarding number; a second writer races Google and silently kills call
    // conversions. Reproduced on production 2026-07-24 — hydrate() reverted the
    // swapped number on 3 of 4 links.
    //
    // Do not re-add [data-sc-loc-phone] / [data-sc-loc-tel-href] handling here.

    root.querySelectorAll('[data-sc-loc-address]').forEach(function (el) {
      el.textContent = loc.address;
    });
    root.querySelectorAll('[data-sc-loc-label]').forEach(function (el) {
      el.textContent = loc.label;
    });
    root.querySelectorAll('[data-sc-loc-page-href]').forEach(function (a) {
      a.setAttribute('href', loc.pageUrl);
    });

    // Switcher menu: visible state (highlight + checkmark) is driven by html[data-sc-loc]
    // selectors (see location-switcher.liquid). Here we only update aria-selected for a11y.
    root.querySelectorAll('[data-sc-loc-option]').forEach(function (li) {
      li.setAttribute('aria-selected', li.getAttribute('data-sc-loc-option') === key ? 'true' : 'false');
    });
  }

  function syncCartAttribute() {
    if (!window.fetch) return;
    var loc = current();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: { location: loc } })
    }).catch(function () { /* swallow — cart might not exist yet */ });
  }

  // pricing(zip?) — pass the ZIP the customer typed and the serving plant's config wins
  // over the cookie, so distance, tax and fees all come from the same market.
  // Called with no argument it behaves as before (cookie-driven).
  function pricing(zip) {
    return get((zip && plantForZip(zip)) || current()).pricing;
  }

  // ─────────────────────────────────────────────────────────────────────────
  //  DISTANCE → DELIVERY — single source of truth for ALL calculators
  //  (home calc in main-custom.js AND sections/price-list.liquid).
  // ─────────────────────────────────────────────────────────────────────────

  // Per-trip delivery $ by driving miles. Returns null when no band matches
  // (miles 0, > 100, or NaN) so callers fall back to pricing().defaultDeliveryPrice.
  function deliveryRate(miles) {
    if (!(miles > 0)) return null;
    if (miles <= 10) return 300;
    if (miles <= 15) return 350;
    if (miles <= 25) return 400;
    if (miles <= 30) return 450;
    if (miles <= 40) return 500;
    if (miles <= 100) return 550;
    return null;
  }

  // Which plant serves this ZIP. The ZIP the customer typed is a stronger signal than
  // the sc_location cookie: the cookie survives for a year, so someone who once opened
  // the NY page would otherwise be quoted from the NY plant for an LA jobsite forever.
  // Returns null for ZIPs outside both regions — callers fall back to current().
  function plantForZip(zip) {
    var n = parseInt(zip, 10);
    if (!(n > 0)) return null;
    if (n >= 90001 && n <= 96162) return 'la';   // California
    if (n >= 10001 && n <= 14975) return 'ny';   // New York State
    // NJ (07xxx-08xxx) / CT (06xxx) are plausible NY-metro jobsites — add here once
    // the NY plant's real service area is confirmed by ops.
    return null;
  }

  // Google Distance Matrix: serving plant's mapsOrigin -> "<zip>, USA".
  //
  // ⚠️ The destination is the BARE ZIP. Never prepend a city: a US ZIP is already
  // unique, and when the city contradicts the ZIP ("New York, 90012, USA") Google
  // silently drops the ZIP and geocodes the city instead — every ZIP then resolves
  // to the same point and the delivery price stops changing. Reproduced 2026-08-04;
  // an earlier variant of the same bug (a ", NY" suffix) was fixed the same way.
  //
  // Calls cb(miles:Number) on success, cb(null) on ANY failure (no google, REQUEST_DENIED,
  // ZERO_RESULTS, missing element). Never throws.
  function distanceMiles(zip, cb) {
    try {
      if (typeof google === 'undefined' || !google.maps || !google.maps.DistanceMatrixService) {
        cb(null); return;
      }
      var P = get(plantForZip(zip) || current()).pricing;
      var service = new google.maps.DistanceMatrixService();
      service.getDistanceMatrix({
        origins: [P.mapsOrigin],
        destinations: [String(zip).trim() + ', USA'],
        travelMode: 'DRIVING',
        unitSystem: google.maps.UnitSystem.IMPERIAL
      }, function (response, status) {
        try {
          if (status !== 'OK' || !response || !response.rows || !response.rows[0]) { cb(null); return; }
          var el = response.rows[0].elements[0];
          if (!el || el.status !== 'OK' || !el.distance) { cb(null); return; }
          cb(el.distance.value / 1609.34);
        } catch (e) { cb(null); }
      });
    } catch (e) { cb(null); }
  }

  window.SCLocation = {
    current: current,
    get: get,
    pricing: pricing,
    deliveryRate: deliveryRate,
    distanceMiles: distanceMiles,
    plantForZip: plantForZip,
    set: function (value) {
      writeCookie(value);
      document.documentElement.setAttribute('data-sc-loc', value);
      hydrate();
      syncCartAttribute();
    },
    hydrate: hydrate,
    syncCartAttribute: syncCartAttribute,
    DATA: DATA
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { hydrate(); });
  } else {
    hydrate();
  }

  if (location.pathname.indexOf('/cart') === 0) {
    syncCartAttribute();
  }

  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('[data-sc-loc-set]');
    if (!a) return;
    var v = a.getAttribute('data-sc-loc-set');
    if (v === 'ny' || v === 'la') {
      writeCookie(v);
    }
  }, true);
})();
